import React from "react";
import { Box, Typography, Stack, Button } from "@mui/material";
import { Link as RouterLink } from "react-router";
import { useTranslation } from "react-i18next";
import SeoHead from "../../Components/SeoHead";
import Hero from "../../Components/Hero";
import FeatureSection from "../../Components/FeatureSection";
import SplitSection from "../../Components/SplitSection";
import InformationSection from "src/Components/InformationSection";
import InformationalCardsSection from "src/Components/InformationalCardsSection";
import { ArrowUpward, Rocket, Star } from "@mui/icons-material";
import { trackEvent } from "src/utils/analytics";
import {
  organizationSchema,
  webPageSchema,
  webSiteSchema,
} from "src/utils/seoSchema";
import { useLocale } from "src/i18n/LocaleProvider";
import {
  audiencePath,
  homePath,
  marketsPath,
} from "src/i18n/paths";
import { MARKET_COUNTRIES, localizedMarketText } from "src/i18n/marketsContent";

type Props = {};

const IndexPage = (props: Props) => {
  const { t } = useTranslation();
  const { locale, config } = useLocale();
  const useAltPlatforms = config.platformsMention === "airbnb-booking";
  const homeDescription = t("home.seoDescription");
  const canonical = homePath(locale);
  const featuredMarkets = MARKET_COUNTRIES.filter(
    (c) =>
      c.primaryLocales.includes(locale) ||
      locale === "en-US"
  );

  return (
    <>
      <SeoHead
        title={t("home.seoTitle")}
        description={homeDescription}
        keywords={t("home.seoKeywords")}
        canonical={canonical}
        ogTitle={t("home.ogTitle")}
        ogDescription={t("home.ogDescription")}
        ogImage="/images/hero.jpg"
        twitterDescription={t("home.ogDescription")}
        jsonLd={[
          organizationSchema(),
          webSiteSchema(),
          {
            ...webPageSchema({
              name: t("home.ogTitle"),
              description: homeDescription,
              url: canonical,
              image: "/images/hero.jpg",
            }),
            short_name: "STR Monitor",
            mainEntity: [
              {
                "@type": "Service",
                name: t("home.featureAlertsTitle"),
                description: t("home.featureAlertsDesc"),
              },
              {
                "@type": "Service",
                name: t("home.featureReportsTitle"),
                description: t("home.featureReportsDesc"),
              },
              {
                "@type": "Service",
                name: t("home.featureLocationTitle"),
                description: t("home.featureLocationDesc"),
              },
              {
                "@type": "Service",
                name: t("home.featureDashboardTitle"),
                description: t("home.featureDashboardDesc"),
              },
            ],
          },
        ]}
      />

      <div>
        <Hero
          title={t("home.heroTitle")}
          subTitle={
            useAltPlatforms
              ? t("home.heroSubtitleAlt")
              : t("home.heroSubtitle")
          }
          buttonText={t("home.contactCta")}
          buttonLink="/contact"
          showRadar
        />

        <InformationSection
          title={t("home.infoTitle")}
          introText={t("home.infoIntro")}
          identificationAndComplianceText={t("home.infoIdentification")}
          firstYearExampleText={t("home.infoFirstYear")}
          adaptabilityText={t("home.infoAdaptability")}
          enforcementText={t("home.infoEnforcement")}
          problemTitle={t("home.problemTitle")}
          problemDescription={
            useAltPlatforms
              ? t("home.problemDescriptionAlt")
              : t("home.problemDescription")
          }
          solutionTitle={t("home.solutionTitle")}
          solutionDescription={t("home.solutionDescription")}
          imageSrc="/images/easy_work_life.jpg"
          imageAlt="Short-Term Rental Illustration"
        />

        <div id="features">
          <FeatureSection
            sectionText={t("home.featuresSection")}
            headerText={t("home.featuresHeader")}
            subText={t("home.featuresSub")}
            cards={[
              {
                image: "/images/email-alert.jpg",
                title: t("home.featureAlertsTitle"),
                description: t("home.featureAlertsDesc"),
              },
              {
                image: "/images/report-creation.jpg",
                title: t("home.featureReportsTitle"),
                description: t("home.featureReportsDesc"),
              },
              {
                image: "/images/location-finder.jpg",
                title: t("home.featureLocationTitle"),
                description: t("home.featureLocationDesc"),
              },
              {
                image: "/images/dashboard.jpg",
                title: t("home.featureDashboardTitle"),
                description: t("home.featureDashboardDesc"),
              },
            ]}
          />
        </div>
        <InformationalCardsSection
          heading={t("home.visibilityHeading")}
          subheading={t("home.visibilitySub")}
          bulletItems={[
            t("home.bulletTax"),
            t("home.bulletOrdinance"),
            t("home.bulletLicensing"),
            t("home.bulletSafety"),
          ]}
          imageSrc="/images/man_working_from_home.jpg"
          imageAlt="Short-Term Rental Monitoring Illustration"
          imagePosition="left"
          cards={[
            {
              icon: <Star fontSize="large" />,
              title: t("home.cardPrecisionTitle"),
              description: t("home.cardPrecisionDesc"),
            },
            {
              icon: <Rocket fontSize="large" />,
              title: t("home.cardEfficiencyTitle"),
              description: t("home.cardEfficiencyDesc"),
            },
            {
              icon: <ArrowUpward fontSize="large" />,
              title: t("home.cardRevenueTitle"),
              description: t("home.cardRevenueDesc"),
            },
          ]}
        />

        <Box
          sx={{
            width: "100%",
            bgcolor: "#070a10",
            py: { xs: 6, md: 8 },
            px: { xs: 2, sm: 3, lg: 4 },
          }}
        >
          <Box sx={{ maxWidth: 1440, mx: "auto", textAlign: "center" }}>
            <Typography
              variant="h4"
              component="h2"
              sx={{
                color: "#fff",
                fontWeight: 700,
                mb: 1.5,
                fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.125rem" },
              }}
            >
              {t("home.whoWeHelp")}
            </Typography>
            <Typography
              sx={{
                color: "#94a3b8",
                mb: 4,
                maxWidth: 720,
                mx: "auto",
                lineHeight: 1.7,
              }}
            >
              {t("home.whoWeHelpSub")}
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="center"
              useFlexGap
              flexWrap="wrap"
            >
              {[
                {
                  label: t("common.cities"),
                  path: audiencePath("cities", locale),
                  blurb: t("home.citiesBlurb"),
                },
                {
                  label: t("common.localGovernments"),
                  path: audiencePath("local-governments", locale),
                  blurb: t("home.localGovBlurb"),
                },
                {
                  label: t("common.hoas"),
                  path: audiencePath("hoas", locale),
                  blurb: t("home.hoasBlurb"),
                },
              ].map((item) => (
                <Button
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  variant="outlined"
                  onClick={() =>
                    trackEvent("cta_click", {
                      cta_id: "home_audience_path",
                      location: "home_who_we_help",
                      destination: item.path,
                      label: item.label,
                    })
                  }
                  sx={{
                    borderColor: "rgba(139, 92, 246, 0.45)",
                    color: "#fff",
                    textTransform: "none",
                    px: 3,
                    py: 1.5,
                    borderRadius: "12px",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    minWidth: { sm: 220 },
                    "&:hover": {
                      borderColor: "#a78bfa",
                      bgcolor: "rgba(139, 92, 246, 0.1)",
                    },
                  }}
                >
                  <Typography sx={{ fontWeight: 700, color: "#d8b4fe" }}>
                    {item.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      color: "#94a3b8",
                      mt: 0.5,
                      textAlign: "left",
                    }}
                  >
                    {item.blurb}
                  </Typography>
                </Button>
              ))}
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            width: "100%",
            bgcolor: "#0b0f17",
            py: { xs: 5, md: 7 },
            px: { xs: 2, sm: 3, lg: 4 },
          }}
        >
          <Box sx={{ maxWidth: 1440, mx: "auto", textAlign: "center" }}>
            <Typography
              variant="h4"
              component="h2"
              sx={{
                color: "#fff",
                fontWeight: 700,
                mb: 1.5,
                fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.125rem" },
              }}
            >
              {t("home.marketsHeading")}
            </Typography>
            <Typography
              sx={{
                color: "#94a3b8",
                mb: 4,
                maxWidth: 720,
                mx: "auto",
                lineHeight: 1.7,
              }}
            >
              {t("home.marketsSub")}
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="center"
              useFlexGap
              flexWrap="wrap"
            >
              {featuredMarkets.map((country) => (
                <Button
                  key={country.slug}
                  component={RouterLink}
                  to={marketsPath(country.slug, locale)}
                  variant="outlined"
                  sx={{
                    borderColor: "rgba(139, 92, 246, 0.45)",
                    color: "#fff",
                    textTransform: "none",
                    px: 3,
                    py: 1.5,
                    borderRadius: "12px",
                    "&:hover": {
                      borderColor: "#a78bfa",
                      bgcolor: "rgba(139, 92, 246, 0.1)",
                    },
                  }}
                >
                  {localizedMarketText(country.name, locale)}
                </Button>
              ))}
            </Stack>
          </Box>
        </Box>

        <SplitSection
          image="/images/building-neighbors.png"
          header={
            config.usesCensusPricing
              ? t("home.nationwideHeader")
              : t("home.nationwideHeaderIntl", { country: config.label })
          }
          description={[
            config.usesCensusPricing
              ? t("home.nationwideDesc1")
              : t("home.nationwideDesc1Intl", { country: config.label }),
            t("home.nationwideDesc2"),
            t("home.nationwideDesc3"),
            t("home.nationwideDesc4"),
            t("home.nationwideDesc5"),
          ]}
          buttonText={t("home.contactCta")}
          buttonLink="/contact"
        />
      </div>
    </>
  );
};

export default IndexPage;
